import { useState, useEffect } from 'react'
import { DialogTitle } from '@headlessui/react'
import SimpleModal from '../../../components/SimpleModal'
import { blastRadiusFor, getClassifierById } from '../mockData'
import BlastRadiusTree from './BlastRadiusTree'

interface Props {
    open: boolean
    onClose: () => void
    classifierId: string
    classifierName: string
}

export default function DeleteClassifierModal({ open, onClose, classifierId, classifierName }: Props) {
    const [typed, setTyped] = useState('')

    // Reset confirm input whenever the modal is opened so stale text never persists.
    useEffect(() => {
        if (open) setTyped('')
    }, [open])

    const classifier = getClassifierById(classifierId)
    const versionCount = classifier?.versions.length ?? 0
    // Use the classifier's profilesUsing count as the authoritative profile total.
    const profileCount = classifier?.profilesUsing ?? 0
    const teamCount = classifier?.teamsUsing ?? 0
    const orgs = blastRadiusFor(classifierId)
    // Mock figure matching the Go Live modal — with profile-only applications the
    // real value would be derived from which orgs the applied profiles belong to.
    const orgCount = 8

    return (
        <SimpleModal open={open} onClose={onClose}>
            <div className="w-[600px] max-w-[94vw] rounded-xl bg-white shadow-2xl text-sm text-gray-800 text-left overflow-hidden">
                {/* Header */}
                <div className="px-5 py-4 border-b border-severe-100 bg-severe-50">
                    <div className="flex items-start gap-3">
                        {/* Danger icon */}
                        <div
                            className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-severe-100 text-severe"
                            aria-hidden="true"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                className="h-4 w-4"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div>
                            <DialogTitle as="h3" className="font-sans !font-medium text-lg text-gray-900 m-0">
                                Delete &ldquo;{classifierName}&rdquo;?
                            </DialogTitle>
                            <p className="text-gray-500 text-[12.5px] mt-1 mb-0">
                                This permanently removes the classifier and all{' '}
                                <strong>{versionCount}</strong> versions.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="px-5 py-4 max-h-[430px] overflow-y-auto space-y-4">
                    {/* Danger warning block */}
                    <div className="rounded-lg border border-severe-200 bg-severe-50 px-4 py-3 text-[12.5px] text-severe">
                        This classifier is currently <strong>live</strong>. Deleting it stops that
                        content from being checked. <strong>This cannot be undone.</strong>
                    </div>

                    {/* What will be affected */}
                    <div>
                        <p className="text-[10.5px] font-bold uppercase tracking-wide text-gray-500 mb-2">
                            What will be affected
                        </p>
                        <p className="text-[12.5px] text-gray-600 mb-2">
                            Removed from{' '}
                            <strong>{profileCount} profiles</strong> across{' '}
                            <strong>{teamCount} teams</strong> in{' '}
                            <strong>{orgCount} organisations</strong>:
                        </p>
                        <BlastRadiusTree
                            orgs={orgs}
                            moreText="+ 5 more organisations · 73 profiles"
                        />
                    </div>

                    {/* Type-to-confirm */}
                    <div>
                        <label
                            htmlFor="delete-confirm-input"
                            className="block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5"
                        >
                            Type <span className="text-severe font-bold">{classifierName}</span> to
                            confirm deletion
                        </label>
                        <input
                            id="delete-confirm-input"
                            type="text"
                            placeholder="Classifier name"
                            value={typed}
                            onChange={(e) => setTyped(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-severe-400 focus:outline-none focus:ring-1 focus:ring-severe-300"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 border-t border-gray-100 bg-gray-50 px-5 py-3.5">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        disabled={typed !== classifierName}
                        className="rounded-lg bg-severe px-4 py-2 text-sm font-bold text-white hover:bg-severe-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        Delete classifier
                    </button>
                </div>
            </div>
        </SimpleModal>
    )
}
